import { Add, CheckCircle, ChevronRight, Schedule, Store } from '@mui/icons-material';
import { Avatar, Box, Button, Card, CardActionArea, Chip, Stack, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useFetch } from '../../../hooks/useFecth';
import { EmptyContentComponent } from '../../../components/Content';
import { LoadingContent } from '../../../components/Loading';
import ListPageLayout from '../../../components/ListPageLayout';
import DialogMigrate from '../../DialogMigrate';
import DialogAdvertiser from './components/DialogAdvertiser';
import { SearchDrawer } from './components/SearchDrawer';

function formatDateTime(value) {
    if (!value) return null;
    try {
        const d = typeof value === 'string' ? parseISO(value) : new Date(value);
        if (!isValid(d)) return null;
        return format(d, 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch {
        return null;
    }
}

function AdvertiserItem({ item, viewMode }) {
    const updatedLabel = formatDateTime(item.updatedAt);
    const createdLabel = formatDateTime(item.createdAt);
    const brokers = item.brokers || [];

    if (viewMode === 'cards') {
        return (
            <Card variant="outlined" sx={{ height: '100%' }}>
                <CardActionArea component={Link} to={`${item.id}`} sx={{ p: 2, height: '100%' }}>
                    <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar src={item.image} variant="rounded" sx={{ width: 48, height: 48 }} />
                            <Box minWidth={0}>
                                <Typography fontWeight={700} noWrap>
                                    {item.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    #{item.id}
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                            <Chip size="small" label={item.status ? 'Ativo' : 'Inativo'} color={item.status ? 'success' : 'default'} variant="outlined" />
                            {item.approved !== undefined && (
                                <Chip size="small" label={item.approved ? 'Aprovado' : 'Pendente'} color={item.approved ? 'success' : 'warning'} variant="outlined" />
                            )}
                            {brokers.map(b => (
                                <Chip key={`${b.broker}-${b.brandId}`} size="small" label={b.broker} variant="outlined" />
                            ))}
                        </Stack>
                        {updatedLabel && (
                            <Typography variant="caption" color="text.secondary">
                                Atualizado {updatedLabel}
                            </Typography>
                        )}
                    </Stack>
                </CardActionArea>
            </Card>
        );
    }

    return (
        <Card variant="outlined">
            <CardActionArea component={Link} to={`${item.id}`} sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 3 }} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Avatar
                        src={item.image}
                        variant="rounded"
                        sx={{ width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 }, border: '1px solid', borderColor: 'divider' }}
                    />
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 600 }}>
                                {item.name}
                            </Typography>
                            <Chip label={`#${item.id}`} size="small" sx={{ fontWeight: 600 }} />
                            {brokers.map(b => (
                                <Chip
                                    key={`${b.broker}-${b.brandId}`}
                                    label={b.broker}
                                    size="small"
                                    color={b.broker === 'awin' ? 'secondary' : 'info'}
                                    variant="outlined"
                                />
                            ))}
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} flexWrap="wrap">
                            <Stack direction="row" spacing={1} alignItems="center">
                                {item.status ? <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} /> : <Schedule sx={{ fontSize: 18, color: 'error.main' }} />}
                                <Typography variant="body2" color="text.secondary">{item.status ? 'Ativo' : 'Inativo'}</Typography>
                            </Stack>
                            {item.approved !== undefined && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    {item.approved ? <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} /> : <Schedule sx={{ fontSize: 18, color: 'warning.main' }} />}
                                    <Typography variant="body2" color="text.secondary">{item.approved ? 'Aprovado' : 'Pendente'}</Typography>
                                </Stack>
                            )}
                            {item.Club && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Store sx={{ fontSize: 18, color: 'action.active' }} />
                                    <Typography variant="body2" color="text.secondary">Indicado por {item.Club.name}</Typography>
                                </Stack>
                            )}
                            <Typography variant="body2" color="text.secondary">{item.Branches?.length || 0} filiais</Typography>
                            <Typography variant="body2" color="text.secondary">{item.Offer?.length || 0} ofertas</Typography>
                            {updatedLabel && <Typography variant="body2" color="text.secondary">Atualizado {updatedLabel}</Typography>}
                            {createdLabel && createdLabel !== updatedLabel && (
                                <Typography variant="body2" color="text.secondary">Criado {createdLabel}</Typography>
                            )}
                        </Stack>
                    </Stack>
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <ChevronRight sx={{ fontSize: 24, color: 'action.active' }} />
                    </Box>
                </Stack>
            </CardActionArea>
        </Card>
    );
}

export default function Advertiser() {
    const [searchParams, setParams] = useSearchParams();
    const [isDrawerOpen, setDrawerOpen] = useState(false);

    const params = useMemo(() => Object.fromEntries(searchParams.entries()), [searchParams]);
    const { data, mutate, isFetching } = useFetch('advertiser', { params });
    const { data: statsData, mutate: mutateStats } = useFetch('advertiser/stats');

    const handleClearFilters = () => setParams({});

    const handlePage = (_, page) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('page', page);
        setParams(nextParams);
    };

    const handlePageSizeChange = nextSize => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('per_page', String(nextSize));
        nextParams.set('page', '1');
        setParams(nextParams);
    };

    const pageSize = Number(searchParams.get('per_page')) || 10;

    const hasFilters = Array.from(searchParams.entries()).some(
        ([key, value]) => value !== '' && value !== null && key !== 'page' && key !== 'per_page',
    );

    const applyChipFilter = nextFilter => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('page');
        nextParams.delete('approved');
        nextParams.delete('status');
        nextParams.delete('missingCategory');
        if (nextFilter) {
            Object.entries(nextFilter).forEach(([key, value]) => {
                if (value === '' || value == null) nextParams.delete(key);
                else nextParams.set(key, value);
            });
        }
        setParams(nextParams);
    };

    const pendingSelected = searchParams.get('approved') === 'false';
    const noCategorySelected = searchParams.get('missingCategory') === 'true';
    const activeSelected = searchParams.get('status') === 'true' && !pendingSelected && !noCategorySelected;
    const inactiveSelected = searchParams.get('status') === 'false' && !pendingSelected && !noCategorySelected;
    const allSelected = !pendingSelected && !activeSelected && !inactiveSelected && !noCategorySelected;

    const counts = [
        {
            key: 'all',
            label: 'anunciantes',
            value: statsData?.total ?? data?.numRows ?? 0,
            color: 'primary',
            selected: allSelected,
            onClick: () => applyChipFilter(null),
        },
        {
            key: 'pending',
            label: 'pendentes',
            value: statsData?.pending ?? 0,
            color: 'warning',
            selected: pendingSelected,
            onClick: () => applyChipFilter(pendingSelected ? null : { approved: 'false' }),
        },
        {
            key: 'active',
            label: 'ativos',
            value: statsData?.active ?? 0,
            color: 'success',
            selected: activeSelected,
            onClick: () => applyChipFilter(activeSelected ? null : { status: 'true' }),
        },
        {
            key: 'inactive',
            label: 'inativos',
            value: statsData?.inactive ?? 0,
            selected: inactiveSelected,
            onClick: () => applyChipFilter(inactiveSelected ? null : { status: 'false' }),
        },
        {
            key: 'noCategory',
            label: 'sem categoria',
            value: statsData?.withoutCategory ?? 0,
            selected: noCategorySelected,
            onClick: () => applyChipFilter(noCategorySelected ? null : { missingCategory: 'true' }),
        },
    ];

    return (
        <>
            <ListPageLayout
                title="Anunciantes"
                subtitle="Gerencie os anunciantes cadastrados no sistema."
                counts={counts}
                loading={isFetching && !data}
                filterActive={hasFilters}
                onClearFilters={hasFilters ? handleClearFilters : null}
                onFiltersClick={() => setDrawerOpen(true)}
                viewStorageKey="admin-advertisers-view"
                actions={
                    <>
                        <Button size="small" variant="outlined" component={Link} to="#migration|advertiser">
                            Migrar
                        </Button>
                        <Button size="small" variant="contained" startIcon={<Add />} component={Link} to="#advertiser">
                            Adicionar
                        </Button>
                    </>
                }
                isEmpty={!isFetching && data?.rows?.length === 0}
                empty={
                    <Stack spacing={2} alignItems="center">
                        <EmptyContentComponent />
                        <Typography color="text.secondary">
                            {hasFilters ? 'Nenhum anunciante com esses filtros.' : 'Nenhum anunciante cadastrado.'}
                        </Typography>
                        {hasFilters ? (
                            <Button variant="outlined" onClick={handleClearFilters}>Limpar filtros</Button>
                        ) : (
                            <Button variant="contained" startIcon={<Add />} component={Link} to="#advertiser">
                                Cadastrar primeiro anunciante
                            </Button>
                        )}
                    </Stack>
                }
                pagination={{
                    page: data?.page || 1,
                    pages: data?.pages || 1,
                    total: data?.numRows ?? 0,
                    pageSize,
                    onChange: handlePage,
                    onPageSizeChange: handlePageSizeChange,
                    itemLabel: (data?.numRows ?? 0) === 1 ? 'anunciante' : 'anunciantes',
                }}
            >
                {({ viewMode }) =>
                    isFetching && !data ? (
                        <LoadingContent />
                    ) : (
                        <Box
                            sx={{
                                p: 2,
                                display: viewMode === 'cards' ? 'grid' : 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                gridTemplateColumns:
                                    viewMode === 'cards'
                                        ? { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }
                                        : undefined,
                            }}
                        >
                            {data?.rows?.map(item => (
                                <AdvertiserItem key={item.id} item={item} viewMode={viewMode} />
                            ))}
                        </Box>
                    )
                }
            </ListPageLayout>

            <DialogAdvertiser mutate={mutate} mutateStats={mutateStats} />
            <DialogMigrate mutate={mutate} />
            <SearchDrawer open={isDrawerOpen} onClose={() => setDrawerOpen(false)} onClearFilters={handleClearFilters} />
        </>
    );
}
